// 3rd Party
import { createSelector } from 'reselect';
import { get, find } from 'lodash';

// Other
import { RequestOffer } from 'connect/saga/handlers/offer/request-offer';

import { RequestReferralPromotion } from 'connect/saga/handlers/offer/request-referral-promotion';

import {
  selectDataClinicProviders,
  selectDataClinicSlots,
} from '../../profile/connect/selectors';
import {
  selectDataAddressState,
  selectDataLocality,
  selectDataMatchId,
  selectDataInsurance,
  selectDataCheckoutFormData,
  selectCJTags,
} from '../../../connect/selectors';

// internal
const selectProviders = state => get(state.data, 'clinic.providers');

const selectSelectedProvider = state =>
  get(state.data, 'requestedAppointment.providerId');

// exports
export const selectAppointmentRequestedId = state =>
  get(state.data, 'appointment.id');

export const selectReason = state => get(state.data, 'match.other.reason');

export const selectReasonRaw = state =>
  get(state.data, 'match.other.reasonRaw');

export const selectSelectedTreatments = state =>
  get(state.data, 'match.other.selectedTreatments');

export const selectOngoingCare = state =>
  get(state.data, 'match.other.ongoingCare');

export const selectOngoingCareNoMatch = state =>
  get(state.data, 'ongoingCareNoMatch');

export const selectReasonNoMatch = state => get(state.data, 'reasonNoMatch');

export const selectRequestedAppointment = state =>
  get(state.data, 'requestedAppointment');

export const selectRequestedSlot = createSelector(
  selectRequestedAppointment,
  selectDataClinicSlots,
  (requestedAppointment, slots) =>
    find(slots, { id: requestedAppointment.slotId }),
);

export const selectRequestedProvider = createSelector(
  selectRequestedAppointment,
  selectDataClinicProviders,
  (requestedAppointment, providers) =>
    find(providers, { id: requestedAppointment.providerId }),
);

export const selectAnyProviderSelected = state =>
  get(state.data, 'requestedAppointment.anyProviderSelected');

export const selectSpecificAvailabilities = state =>
  get(state.data, 'match.other.specificAvailabilities');

export const selectSpecialtyId = state => get(state.data, 'match.specialty.id');

export const selectSpecialtyName = state =>
  get(state.data, 'match.specialty.name');

export const selectTrafficSource = state =>
  get(state.data, 'match.other.trafficSource');

export const selectTrafficSourceLastTouch = state =>
  get(state.data, 'match.other.trafficSourceLastTouch');

export const selectUnbounceProps = state =>
  get(state.data, 'match.other.unbounceProps');

export const selectUtmTags = state => {
  const utmTags = get(state.data, 'match.other.utmTags', {});

  // ----------------- BUG FIX #3088 ----------------- //
  // https://rollbar.com/opencare/search-v2/items/3088/
  // Can likely be removed in a few weeks
  if (utmTags.source && Array.isArray(utmTags.source)) {
    utmTags.source = utmTags.source[utmTags.source.length - 1];
  }

  if (utmTags.medium && Array.isArray(utmTags.medium)) {
    utmTags.medium = utmTags.medium[utmTags.medium.length - 1];
  }
  // ------------------ END BUG FIX ------------------ //

  return utmTags;
};

export const selectDmProps = state =>
  get(state.data, 'match.other.dmProps', {});

export const selectAffiliates = state =>
  get(state.data, 'match.other.affiliates');

export const selectDataCheckoutFormErrorStatus = state =>
  get(state.data, 'checkoutFormErrorStatus');

export const makeSelectRequestedAppointmentProvider = createSelector(
  selectSelectedProvider,
  selectProviders,
  (providerId, providers) => providers.find(p => p.id === providerId),
);

export const makeSelectRequestData = createSelector(
  selectUtmTags,
  RequestReferralPromotion.value.selector,
  RequestOffer.value.selector,
  selectDmProps,
  selectUnbounceProps,
  selectReasonRaw,
  selectSelectedTreatments,
  selectOngoingCare,
  selectDataMatchId,
  selectSpecialtyName,
  selectDataAddressState,
  selectDataLocality,
  selectTrafficSource,
  selectTrafficSourceLastTouch,
  selectAffiliates,
  selectCJTags,
  (
    utmTags,
    referralPromotion,
    offer,
    dmProps,
    unbounceProps,
    reasonRaw,
    selectedTreatments,
    ongoingCare,
    matchId,
    specialtyName,
    state,
    locality,
    trafficSource,
    trafficSourceLastTouch,
    _affiliates,
    cjTags,
  ) => ({
    visitedBefore: false,
    source: 'recommendations',
    utmTags,
    dmProps:
      get(offer, 'active') || Object.keys(dmProps).length > 0
        ? {
          promo_code: get(offer, 'active') ? offer.slug : '',
          referral_code: get(dmProps, 'referral_code', ''),
          referral_promo: get(dmProps, 'referral_promo', ''),
          referral_promo_id: get(referralPromotion, 'id'),
        }
        : undefined,
    unbounceProps,
    requestedSlotOptionsExperiment: [],
    activeExperiments: {},
    reason: {
      overall: {
        name: get(reasonRaw, 'overall.name'),
        id: get(reasonRaw, 'overall.id'),
      },
      ...selectedTreatments?.length && {
        selectedTreatments,
      },
    },
    match: matchId
      ? {
        id: matchId,
        specialty: specialtyName,
        state,
        locality,
      }
      : undefined,
    ongoingCare,
    trafficSource,
    trafficSourceLastTouch,
    cjEvent: get(cjTags, 'event'),
    cjSid: get(cjTags, 'sid'),
    cjAid: get(cjTags, 'aid'),
    cjPid: get(cjTags, 'pid'),
    cjLinkOpenedAt: get(cjTags, 'linkOpenedAt')
      ? parseInt(cjTags.linkOpenedAt, 10)
      : undefined,
  }),
);

export const makeSelectInsuranceData = createSelector(
  selectDataInsurance,
  selectDataCheckoutFormData,
  (insurance, checkoutFormData) => ({
    provider: insurance.provider || '',
    providerId: insurance.providerId || '',
    providerPlan: insurance.plan || '',
    planId: insurance.planId || '',
    groupNumber: checkoutFormData.groupNumber || '',
    memberId: insurance.memberId || '',
    employer: checkoutFormData.employer || '',
    inNetwork: insurance.inNetwork || '',
    isBenefits: insurance.isBenefits || '',
    isPrimaryHolder: checkoutFormData.isPrimaryMember,
    socialAssistance: insurance.socialAssistance,
    insuranceFeedbackId: insurance.insuranceFeedbackId,
  }),
);
